/**
 * @voltxampere/ui — Formula registry (Sprint 14, ADR-0020)
 *
 * Component type → KaTeX-renderable formula template. Each entry
 * is a `FormulaTemplate` with three pieces:
 *
 *   - `symbolic` — the abstract law (e.g. `V = I \cdot R`).
 *   - `numeric`  — optional, populated when a DC result is in
 *      hand. Renders the live operating point ("V = 0.5\,
 *      \text{A} \cdot 1\,\text{k}\Omega = 500\,\text{mV}") so the
 *      inspector can show "what the law says right now" without
 *      losing the abstract form.
 *   - `vars`     — token list for Zenit voice highlighting.
 *      `katex-renderer.applyZenitVoice` walks the rendered DOM
 *      and tags `vars` entries as italic / brass.
 *
 * The 18 entries below cover Sprint 10.1's symbol library minus
 * `wire` and `node` (electrical primitives without per-instance
 * laws — they live in the net-mode inspector instead). Bilinmeyen
 * tip için `getFormula` `null` döner; inspector "Bu bileşen için
 * formül tanımlı değil" placeholder gösterir (Kural 9 — fake
 * fallback yok).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
/**
 * Pure registry — no live data injected. Inspector calls
 * `instantiate(template, dcSnapshot)` separately when it has DC
 * outputs to splice in. Sprint 14 ships the symbolic form; the
 * numeric instantiation helper is a Sprint 15 follow-up (the
 * `numeric` field is reserved here).
 */
export const FORMULA_REGISTRY = Object.freeze({
    resistor: {
        symbolic: "V = I \\cdot R",
        vars: ["V", "I", "R"],
    },
    capacitor: {
        symbolic: "I = C \\frac{dV}{dt}",
        vars: ["I", "C", "V", "t"],
    },
    inductor: {
        symbolic: "V = L \\frac{dI}{dt}",
        vars: ["V", "L", "I", "t"],
    },
    potentiometer: {
        symbolic: "V_{\\text{out}} = V_{\\text{in}} \\cdot \\frac{R_2}{R_1 + R_2}",
        vars: ["V"],
    },
    switch: {
        symbolic: "R = \\begin{cases} 0 & \\text{kapalı} \\\\ \\infty & \\text{açık} \\end{cases}",
        vars: ["R"],
    },
    ground: {
        symbolic: "V = 0",
        vars: ["V"],
    },
    "dc-source": {
        symbolic: "V(t) = V_0",
        vars: ["V", "V_0", "t"],
    },
    "ac-source": {
        symbolic: "V(t) = A \\sin(2\\pi f t + \\phi)",
        vars: ["V", "A", "f", "t", "\\phi"],
    },
    "pulse-source": {
        symbolic: "V(t) = \\begin{cases} V_1 & 0 \\le t < t_1 \\\\ V_2 & t_1 \\le t < t_2 \\end{cases}",
        vars: ["V", "t", "V_1", "V_2", "t_1", "t_2"],
    },
    diode: {
        symbolic: "I = I_S \\left(e^{V / (n V_T)} - 1\\right)",
        vars: ["I", "I_S", "V", "V_T", "n"],
    },
    led: {
        symbolic: "I = I_S \\left(e^{V / V_T} - 1\\right), \\quad V_F \\approx 2\\,\\text{V}",
        vars: ["I", "I_S", "V", "V_T", "V_F"],
    },
    zener: {
        symbolic: "I = \\begin{cases} I_S(e^{V/V_T}-1) & V > -V_Z \\\\ -I_Z & V \\le -V_Z \\end{cases}",
        vars: ["I", "V", "V_Z", "V_T", "I_S", "I_Z"],
    },
    npn: {
        symbolic: "I_C = \\beta I_B, \\quad V_{BE} \\approx 0.7\\,\\text{V}",
        vars: ["I_C", "\\beta", "I_B", "V_{BE}"],
    },
    pnp: {
        symbolic: "I_C = \\beta I_B, \\quad V_{EB} \\approx 0.7\\,\\text{V}",
        vars: ["I_C", "\\beta", "I_B", "V_{EB}"],
    },
    nmos: {
        symbolic: "I_D = \\tfrac{1}{2} \\mu_n C_{ox} \\tfrac{W}{L} (V_{GS} - V_{TH})^2",
        vars: ["I_D", "\\mu_n", "C_{ox}", "W", "L", "V_{GS}", "V_{TH}"],
    },
    pmos: {
        symbolic: "I_D = \\tfrac{1}{2} \\mu_p C_{ox} \\tfrac{W}{L} (V_{SG} - |V_{TH}|)^2",
        vars: ["I_D", "\\mu_p", "C_{ox}", "W", "L", "V_{SG}", "V_{TH}"],
    },
    jfet: {
        symbolic: "I_D = I_{DSS} \\left(1 - \\tfrac{V_{GS}}{V_P}\\right)^2",
        vars: ["I_D", "I_{DSS}", "V_{GS}", "V_P"],
    },
    "op-amp": {
        symbolic: "V_{\\text{out}} = A_0 (V_+ - V_-)",
        vars: ["V_{\\text{out}}", "A_0", "V_+", "V_-"],
    },
});
/**
 * Look up the template for a component instance. Returns `null`
 * when no formula is registered for the type — the inspector
 * shows a "Bu bileşen için formül tanımlı değil" placeholder
 * instead of a fake fallback (Kural 9).
 *
 * Sprint 15 will widen the second argument to a `DCSnapshot` so
 * `numeric` can be populated; for now the parameter is reserved
 * but unused, kept on the signature so call sites do not have to
 * change once Sprint 15 lands.
 */
export function getFormula(component, _dcSnapshot) {
    void _dcSnapshot;
    return FORMULA_REGISTRY[component.type] ?? null;
}
//# sourceMappingURL=formula-registry.js.map